import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { DEFAULT_API_ADMIN } from '../constants';
import * as authManager from '../lib/authManager';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'https://socket-server.pontuax.com.br';
const TOKEN_REFRESH_MARGIN_MS = 60 * 1000; // renovar 60s antes de expirar

export interface LiveMapEvent {
  reserveId: string;
  stationId: string;
  stationNumericId?: number;
  chainId: string;
  status?: string;
  value?: number;
  volume?: number;
  fuelName?: string;
  startedAt?: string;
}

/**
 * Busca um token JWT de curta duração (5min) específico para o live map socket.
 * Token tem purpose='admin-livemap' — não é reutilizável como token admin normal.
 */
async function fetchSocketToken(): Promise<{ token: string; expiresIn: number }> {
  const accessToken = await authManager.ensure();
  const res = await fetch(`${DEFAULT_API_ADMIN}/employees/socket-token`, {
    method: 'POST',
    headers: {
      Authorization: accessToken || '',
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) throw new Error(`Socket token request failed: ${res.status}`);
  return res.json();
}

/**
 * Hook para conexão Socket.IO segura com o live map.
 *
 * Fluxo:
 * 1. Busca token curto via POST /employees/socket-token
 * 2. Conecta no namespace /admin do socket-server com auth: { token }
 * 3. Server valida JWT e faz join na room correta (por chain ou admin:all)
 * 4. Escuta eventos livemap:update e livemap:remove
 * 5. Auto-renova token 60s antes de expirar
 */
export function useLiveMapSocket(
  onUpdate: (data: LiveMapEvent) => void,
  onRemove: (data: LiveMapEvent) => void,
) {
  const socketRef = useRef<Socket | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [connected, setConnected] = useState(false);

  // Refs pras callbacks — evita reconexão quando handlers mudam
  const onUpdateRef = useRef(onUpdate);
  const onRemoveRef = useRef(onRemove);
  onUpdateRef.current = onUpdate;
  onRemoveRef.current = onRemove;

  const connect = useCallback(async () => {
    try {
      const { token, expiresIn } = await fetchSocketToken();

      // Desconecta socket anterior
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      }

      const socket = io(`${SOCKET_URL}/admin`, {
        auth: { token },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });

      socket.on('connect', () => {
        console.log('[LiveMap Socket] Conectado');
        setConnected(true);
      });

      socket.on('disconnect', (reason) => {
        console.log('[LiveMap Socket] Desconectado:', reason);
        setConnected(false);
      });

      socket.on('connect_error', (err) => {
        console.error('[LiveMap Socket] Erro de conexão:', err.message);
        setConnected(false);
      });

      socket.on('livemap:update', (data: LiveMapEvent) => onUpdateRef.current(data));
      socket.on('livemap:remove', (data: LiveMapEvent) => onRemoveRef.current(data));

      socketRef.current = socket;

      // Agenda renovação do token antes de expirar
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      const refreshIn = Math.max((expiresIn * 1000) - TOKEN_REFRESH_MARGIN_MS, 30000);
      refreshTimerRef.current = setTimeout(() => connect(), refreshIn);

    } catch (err) {
      console.error('[LiveMap Socket] Falha ao conectar:', err);
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    connect();

    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      }
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [connect]);

  return { connected };
}
